import { Injectable, signal, OnDestroy } from '@angular/core';
import {
  AnalyticsInterval,
  AnalyticsRangeContext,
  ComparePeriod,
} from '@services/analytics-services/core';
import { DateRangeEvent, DateRangePresetType, Tab } from '@services/core-services/models';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

import { DateRangeService } from '@enginuity/core/molecules/date-range/date-range.service';
import { toSignal } from '@angular/core/rxjs-interop';

const TABS: Tab[] = [
  {
    label: 'Overview',
    id: 'overview',
  },
  {
    label: 'Orders',
    id: 'orders',
  },
  {
    label: 'Products',
    id: 'products',
  },
  {
    label: 'Customers',
    id: 'customers',
  },
  {
    label: 'Shipment & fulfillment',
    id: 'shipment',
  },
  {
    label: 'Payments',
    id: 'payments',
  },
  {
    label: 'Insights & Analytics',
    id: 'insights',
  },
];

@Injectable()
export class DashboardStore implements OnDestroy {
  //debounce
  public static readonly DEBOUNCE_TIME = 500;
  private readonly comparePeriodSignal = signal<ComparePeriod>(ComparePeriod.Custom);
  private readonly contextSignal;
  private readonly destroy$ = new Subject<void>();

  /**we can remove */
  private comparePeriod = new BehaviorSubject<ComparePeriod>(ComparePeriod.Custom);
  private context = new BehaviorSubject<AnalyticsRangeContext>({
    ...this.getDefaultRange(DateRangePresetType.Today),
    interval: AnalyticsInterval.Hour,
  });

  private tabs = new BehaviorSubject<Tab[]>(TABS);

  constructor(private dateRangeService: DateRangeService) {
    // Create a debounced signal from the context observable
    this.contextSignal = toSignal(this.context.pipe(debounceTime(DashboardStore.DEBOUNCE_TIME)), {
      initialValue: this.context.value,
    });

    this.comparePeriod
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.comparePeriodSignal.set(value));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getRangeContextSignal() {
    return this.contextSignal;
  }

  getComparePeriodSignal() {
    return this.comparePeriodSignal;
  }

  // Utility methods
  private getDefaultRange(preset: DateRangePresetType): DateRangeEvent {
    const { range, compare } = this.dateRangeService.getDateRangeContext(DateRangePresetType.Today);
    return {
      range,
      compare,
      preset,
    };
  }

  // State setters (updates both signals and observables)
  setComparePeriod(period: ComparePeriod) {
    this.comparePeriodSignal.set(period);
    this.comparePeriod.next(period);
  }

  setRangeContext(range: AnalyticsRangeContext) {
    this.context.next(range);
  }

  getRangeContext() {
    return this.context.asObservable();
  }

  getRangeContextSnapshot() {
    return this.contextSignal();
  }

  getComparePeriod() {
    return this.comparePeriod.asObservable();
  }
  getTabs() {
    return this.tabs.asObservable();
  }
}
